import { type BillingViewModelFactory } from './BillingViewModel'
import { type TeamSelection, type QueueChange } from './DraftPersister'
import { SubmissionStatusModalViewModel } from './SubmissionStatusModalViewModel'
import { task } from 'mobx-task'
import { computed } from 'mobx'
import { defer, merge, type Observable, of, Subject } from 'rxjs'
import { extractMessageFromError } from '@/utils/error-util'
import { isAbortError } from '@/utils/abort-util'
import {
  every,
  shareLatest,
  whileActive,
  withAbortSignal,
} from '@/utils/rx-util'
import { fromStream } from '@/utils/mobx-util'
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  first,
  share,
  take,
} from 'rxjs/operators'
import { PublishedLegend } from '@/features/jobs/legends/PublishedLegend'
import { type Member } from '@/features/iam/members/Member'
import type { GetCurrentMemberFn, GetTeamMemberFn } from '@/features/iam'
import { canSubmitDraftJob, canViewJobPrice } from './draft-permissions'
import { Job } from '@/features/jobs/Job'
import { formatWithLegendPriceUnit } from '@/features/jobs/legends/legend-price-unit'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type DraftStateSlice } from './DraftJobViewModel'
import {
  type StepAccessor,
  type StepFactory,
  type StepViewModel,
} from './OutlineViewModel'
import { type DraftJobStore } from './DraftJobStore'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { NavigateFn } from '@/RootStore'
import type { Quote } from './Quote'
import {
  formatQuotePricing,
  formatQuoteSummaryItems,
  type PricingInfo,
  type SummaryDivider,
  type SummaryItem,
} from './quote-display'
import type { Id } from '@/infra/id'

/**
 * Submission View Model.
 */
export class SubmissionViewModel
  implements ActivationLifecycle, DraftStateSlice
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Used to emit when the request has been submitted.
   * @private
   */
  private readonly _onSubmitted = new Subject<void>()

  /**
   * Deferred for the visibility observable.
   *
   * @private
   */
  private readonly _isVisible$ = defer(() => this.step.isVisible$)

  /**
   * Emits when we should trigger a quote fetch.
   * The first time the step becomes visible, trigger
   * a fetch, after that we only trigger a fetch when told to refresh.
   * If told to refresh while not visible, we wait until we become visible.
   *
   * @private
   */
  private readonly _triggerQuoteFetch$ = this._isVisible$.pipe(
    first(Boolean),
    switchMap(() =>
      merge(
        of(null),
        this._refresh$.pipe(
          switchMap(() => this._isVisible$.pipe(first(Boolean))),
        ),
      ),
    ),
  )

  /**
   * The step VM.
   */
  readonly step: StepViewModel

  /**
   * Emits when the request has been submitted.
   */
  readonly submitted$ = this._onSubmitted.asObservable()

  /**
   * Whether we have enough data to present the breakdown.
   * Emits the first value only after all the data it depends on
   * is available. This is key to ensuring we don't fetch until then.
   *
   * @private
   */
  private readonly _canFetchQuote$

  /**
   * Quote query pipeline.
   *
   * @private
   */
  private readonly _quoteQuery$: Observable<Quote | null>

  /**
   * The latest quote.
   *
   * @private
   */
  private readonly _quote$

  /**
   * Emits whenever loading indicators should start.
   *
   * @private
   */
  private readonly _loadingStart$

  /**
   * Whether we are currently loading the quote.
   *
   * @private
   */
  private readonly _quoteLoading$

  /**
   * Billing.
   */
  readonly billing

  /**
   * Submission status.
   */
  readonly submissionStatus

  /**
   * Streams as observable.
   *
   * @private
   */
  private readonly _$

  /**
   * Constructor.
   */
  constructor(
    createStep: StepFactory,
    private readonly _draftJobStore: DraftJobStore,
    private readonly _navigate: NavigateFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _createBillingViewModel: BillingViewModelFactory,
    private readonly _queueChange: QueueChange,
    private readonly _member$: Observable<Member | null>,
    private readonly _team$: Observable<TeamSelection | null>,
    private readonly _job$: Observable<Job>,
    private readonly _legend$: Observable<PublishedLegend | null>,
    private readonly _allQuestionsAnswered$: Observable<boolean>,
    private readonly _refresh$: Observable<unknown>,
    private readonly _persisting$: Observable<boolean>,
    private readonly _submit: () => Promise<void>,
    private readonly _isValid: () => boolean,
    private readonly _isDisabled: () => boolean,
    private readonly _copyJob: () => Promise<{ jobId: Id }>,
    private readonly _canEditDraftJobsAsAdmin: () => boolean,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getTeamMember: GetTeamMemberFn,
  ) {
    this._canFetchQuote$ = this._job$.pipe(
      switchMap(() =>
        every([this._allQuestionsAnswered$, this._job$, this._legend$]),
      ),
      shareLatest(),
      this._whileActive(),
    )

    this._quoteQuery$ = this._triggerQuoteFetch$.pipe(
      // We only care about the first emission, since fetches should only happen
      // when the trigger signal emits.
      // NOTE: The reason we use the switch map + first combo here rather than the
      // MobX getter is that this stream won't emit its' first value
      // until it has all the facts, allowing us to defer the initial fetch.
      switchMap(() => this._canFetchQuote$.pipe(first())),
      switchMap((canFetchQuote) =>
        canFetchQuote
          ? withAbortSignal((signal) =>
              this._createAlertRetryWrapper(
                'Loading the quote failed',
                signal,
              )(() => this._fetchQuote(signal)),
            ).pipe(
              // Without a quote the request can't be submitted, so the next
              // refresh gets a chance to fetch it again.
              catchError(() => of(null)),
            )
          : of(null),
      ),
      share(),
      this._whileActive(),
    )

    this._quote$ = this._quoteQuery$.pipe(shareLatest(), this._whileActive())

    this._loadingStart$ = merge(
      // Whenever we trigger a fetch...
      this._triggerQuoteFetch$,
      // OR whenever we start persisting...
      this._persisting$.pipe(filter(Boolean)),
    )

    this._quoteLoading$ = this._loadingStart$.pipe(
      loadingUntil(this._quoteQuery$),
      shareLatest(),
      this._whileActive(),
    )

    this.billing = this._createBillingViewModel(
      this._member$,
      this._team$,
      this._job$,
      this._canFetchQuote$,
      this._queueChange,
      this._isDisabled,
      this._canEditDraftJobsAsAdmin,
      () => this.isAllowedToSubmit,
    )

    this.submissionStatus = new SubmissionStatusModalViewModel(
      this._job$,
      () => this.billing.selectedPaymentMethod,
      this._copyJob,
      () => this.billing.canBypassBilling,
      this._navigate,
      this._createAlertErrorHandler,
    )

    this._$ = {
      canFetchQuote: fromStream(false, this._canFetchQuote$),
      quote: fromStream(null, this._quote$),
      quoteLoading: fromStream(false, this._quoteLoading$),
      legend: fromStream(null, this._legend$),
      job: fromStream(Job.none, this._job$),
    }

    this._lifecycle.manage(this.billing)

    this.step = createStep({
      stepId: 'submission',
      isCompleted: () => this.billing.isValid,
      outlineItemDisplay: () => {
        return {
          title: 'Order Summary',
          description: 'Please confirm that everything looks correct.',
          value: 'Review your order',
        }
      },
    })
  }

  /**
   * Whether we are currently loading the quote.
   */
  @computed
  get isLoading() {
    return this._$.quoteLoading() || this.billing.isLoading
  }

  /**
   * Whether the current user is allowed to submit the request.
   */
  @computed
  get isAllowedToSubmit() {
    return canSubmitDraftJob(
      this._$.job(),
      this._getCurrentMember,
      this._getTeamMember,
    )
  }

  /**
   * Whether we are able to submit the request.
   */
  @computed
  get canSubmit() {
    return (
      this.isAllowedToSubmit &&
      !this.isLoading &&
      Boolean(this.quote) &&
      this._isValid()
    )
  }

  /**
   * Legend info.
   */
  @computed
  get legendInfo(): LegendInfo | null {
    const legend = this._$.legend()
    if (!legend) {
      return null
    }

    return {
      title: legend.name,
      imageUrl: legend.imageUrl,
    }
  }

  /**
   * The latest quote.
   */
  @computed
  get quote() {
    return this._$.quote()
  }

  /**
   * Summary items of the quote, empty when the price may not be shown.
   */
  @computed
  get summaryItems(): (SummaryItem | SummaryDivider)[] {
    const quote = this.quote
    if (!quote || !this._isPriceShown) {
      return []
    }

    return formatQuoteSummaryItems(quote, (v) => this._formatUnit(v))
  }

  /**
   * Pricing of the quote, `null` when the price may not be shown.
   */
  @computed
  get pricing(): PricingInfo | null {
    const quote = this.quote
    if (!quote || !this._isPriceShown) {
      return null
    }

    return formatQuotePricing(quote, (v) => this._formatUnit(v))
  }

  /**
   * Step accessors.
   */
  @computed
  get stepAccessors(): StepAccessor[] {
    return this._$.canFetchQuote() ? [this] : []
  }

  /**
   * Whether the current user may see the quoted price.
   *
   * @private
   */
  @computed
  private get _isPriceShown() {
    const legend = this._$.legend() ?? PublishedLegend.none
    return (
      canViewJobPrice(
        this._$.job(),
        this._getCurrentMember,
        this._getTeamMember,
      ) && !legend.skipQuote
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Prepares the VM.
   */
  async prepare(): Promise<void> {
    // Nothing to do here, we are not preventing any rendering.
    // Quote loads happen asynchronously and reactively.
  }

  /**
   * Submit the request.
   */
  submit = task.resolved(async () => {
    this.submissionStatus.startSubmission()
    try {
      await this._submit()
      this.submissionStatus.submissionSuccessful()
      this._onSubmitted.next()
    } catch (err) {
      if (isAbortError(err)) {
        return
      }

      this.submissionStatus.submissionFailed(extractMessageFromError(err))
    }
  })

  /**
   * Formats an amount in the Legend's price unit.
   *
   * @private
   */
  private _formatUnit(value: number) {
    return formatWithLegendPriceUnit(
      this._$.legend() ?? PublishedLegend.none,
      value,
    )
  }

  /**
   * Fetches the quote, and the coupon it applies.
   */
  private async _fetchQuote(signal: AbortSignal) {
    const quote = await this._draftJobStore.fetchQuote(this._$.job().id, signal)
    await this.billing.fetchCouponInfo(
      quote.price.discount?.couponId ?? null,
      signal,
    )
    return quote
  }

  /**
   * Shortcut for the whileActive operator using the lifecycle's active stream.
   *
   * @private
   */
  private _whileActive<T>() {
    return whileActive<T>(this._lifecycle.active$)
  }
}

/**
 * Legend info.
 */
export interface LegendInfo {
  imageUrl: string | null
  title: string
}

/**
 * Operator used for creating loading observables.
 *
 * @param endSignal$ Used to signal that a load was completed.
 *  NOTE: This observable should abort on unsubscribe!
 */
function loadingUntil(endSignal$: Observable<unknown>) {
  return (startSignal$: Observable<unknown>) =>
    startSignal$.pipe(
      switchMap(() =>
        merge(
          // Emit that we are now loading
          of(true),
          // Once the next query finishes, emit that we are no longer loading
          endSignal$.pipe(
            take(1),
            map(() => false),
          ),
        ),
      ),
      distinctUntilChanged(),
    )
}

/**
 * Factory for creating a {@link SubmissionViewModel}.
 */
export type SubmissionViewModelFactory = ReturnType<
  typeof createSubmissionViewModelFactory
>

/** @see SubmissionViewModelFactory */
export function createSubmissionViewModelFactory(
  draftJobStore: DraftJobStore,
  navigate: NavigateFn,
  createAlertErrorHandler: AlertErrorHandlerFactory,
  createAlertRetryWrapper: AlertRetryWrapperFactory,
  createBillingViewModel: BillingViewModelFactory,
  getCurrentMember: GetCurrentMemberFn,
  getTeamMember: GetTeamMemberFn,
) {
  return (
    createStep: StepFactory,
    queueChange: QueueChange,
    member$: Observable<Member | null>,
    team$: Observable<TeamSelection | null>,
    job$: Observable<Job>,
    legend$: Observable<PublishedLegend | null>,
    allQuestionsAnswered$: Observable<boolean>,
    refresh$: Observable<unknown>,
    persisting$: Observable<boolean>,
    submit: () => Promise<void>,
    isValid: () => boolean,
    isDisabled: () => boolean,
    copyJob: () => Promise<{ jobId: Id }>,
    canEditDraftJobsAsAdmin: () => boolean,
  ) =>
    new SubmissionViewModel(
      createStep,
      draftJobStore,
      navigate,
      createAlertErrorHandler,
      createAlertRetryWrapper,
      createBillingViewModel,
      queueChange,
      member$,
      team$,
      job$,
      legend$,
      allQuestionsAnswered$,
      refresh$,
      persisting$,
      submit,
      isValid,
      isDisabled,
      copyJob,
      canEditDraftJobsAsAdmin,
      getCurrentMember,
      getTeamMember,
    )
}
